import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { useEffect } from 'react';

import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { PAGES } from '../../constants/pages';

import { DOCUMENT_TYPES } from '../../util/constants';
import WebcamIcon from '../../components/Icons/WebcamIcon';
import UploadIcon from '../../components/Icons/UploadIcon';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import KycSelectionContainer from '../../components/KycSelectionContainer';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { useApplicantData } from '../../hooks/useApplicantData';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  CONTENT_TYPE,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import useKycProofOfSocialSecurityDocuments from '../../hooks/useKycSocialSecurityDocuments';
import { selectRequiredDocuments } from '../../store/selectors/documentsSelectors';

const Wrapper = styled.div`
  padding: 50px 0;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 500px) {
    padding: 0;
  }
`;

const KycSSCUploadMethod = ({ applicantType, documentType }) => {
  const { t } = useTranslation();
  const { BrokerName } = useSelector(selectSettings);
  const ApplicantData = useApplicantData(applicantType);
  const requiredDocuments = useSelector(selectRequiredDocuments);
  const { kycSocialSecurityOptions } = useKycProofOfSocialSecurityDocuments(
    requiredDocuments,
    applicantType,
  );
  const sscDocumentOptions = kycSocialSecurityOptions();

  if (!isEmpty(sscDocumentOptions)) {
    storeInSessionStorage(KYC_SELECTED_OPTION, sscDocumentOptions[0]);
  }

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    removeFromSessionStorage(CONTENT_TYPE);
  }, []);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc upload method',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc upload method',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const getTitleTag = () => {
    switch (documentType) {
      case DOCUMENT_TYPES.SOCIAL_SECURITY_CARD:
        return t('kycVerification.titleTagproofOfSocialSecurity', {
          brokerName: BrokerName,
        });

      default:
        break;
    }
  };

  const getUploadFilePath = (value) => {
    switch (documentType) {
      case DOCUMENT_TYPES.SOCIAL_SECURITY_CARD:
        return PAGES.KYC_PROOF_OF_SOCIAL_SECURITY[
          String(applicantType).toUpperCase()
        ][value].SOCIAL_SECURITY_CARD;

      default:
        break;
    }
  };

  const options = [
    {
      title: t('kycVerification.webcam'),
      subtitle: t('kycVerification.webcamDescription'),
      Icon: WebcamIcon,
      path: getUploadFilePath('UPLOAD_CAMERA'),
      onClick: () => {
        handleGtmEvent();
      },
    },
    {
      title: t('kycVerification.fileUpload'),
      subtitle: t('kycVerification.fileUploadDescription'),
      Icon: UploadIcon,
      path: getUploadFilePath('UPLOAD_FILE'),
      onClick: () => {
        handleGtmEvent();
      },
    },
  ];

  const mobileOptions = [
    {
      title: t('kycVerification.fileUpload'),
      subtitle: t('kycVerification.fileUploadDescriptionMobile'),
      Icon: UploadIcon,
      path: getUploadFilePath('UPLOAD_FILE'),
    },
  ];

  const getPreviousPath = () => PAGES.KYC_VERIFICATION;

  return (
    <BaseLayout previousPath={getPreviousPath()} isKycProcess="true" noLogoBox>
      <Helmet>
        <title>{getTitleTag()}</title>
      </Helmet>
      <Wrapper>
        <Header
          title={t('kycVerification.chooseMethod')}
          subtitle={t('kycVerification.chooseFromFollowingSSC')}
          subTitleMb={24}
        />
        {!isMobileOnly
          ? options?.map((option) => (
              <KycSelectionContainer key={option.path} {...option} />
            ))
          : mobileOptions?.map((option) => (
              <KycSelectionContainer key={option.path} {...option} />
            ))}
      </Wrapper>
    </BaseLayout>
  );
};

KycSSCUploadMethod.propTypes = {
  applicantType: PropTypes.string,
  documentType: PropTypes.string,
};

export default KycSSCUploadMethod;
